import React, {MutableRefObject, useImperativeHandle, useMemo, useState} from "react";
import {arrayUpdatePartial, ArrayUpdateType} from "../../immutableState";
import {classNames} from "../../wrapper";
import Input from "../../components/Input";
import TestType from "../../controllers/TestType";
import SubmissionDataResponse from "../../controllers/SubmissionDataResponse";
import {useValidationArray} from "../validationArray";
import GenericSelect from "../../components/GenericSelect";
import {SoilRow, emptySoilRow} from "./SubmissionPrepareSoil";
import SampleDepth from "../../controllers/SampleDepth";
import TableCore, {TableColumn} from "../../components/TableCore";
import {pasteInColumn} from "../pasteColumn";

export interface SubmissionPrepareFunctions {
    validate: () => boolean;
}

const SubmissionTable: React.FC<{
    leafs: (SoilRow)[]
    data: SubmissionDataResponse<SoilRow>
    setData: (data: (SoilRow)[]) => void
    index: number
    submitRef?: MutableRefObject<SubmissionPrepareFunctions | undefined>;
}> = (props) => {

    const [headerTest, setHeaderTest] = useState<TestType | null>(null)
    const [headerSampleDepth, setHeaderSampleDepth] = useState<SampleDepth | null>(null)
    const [headerOptionalTests, setHeaderOptionalTests] = useState<TestType[]>([]);

    useImperativeHandle(props.submitRef, () => ({
        validate: () => validation.validate()
    }));

    function lengthCheck(length: number, deleted: boolean) {
        // if it's deleted, it's fine and the validation should pass
        return length > 0 || deleted;
    }

    function duplicatesampleNumberCheck(sampleNumber: string) {
        return props.leafs.filter(l => l.sampleNumber === sampleNumber).length === 1;
    }

    const validation = useValidationArray(props.leafs)({
        sampleNumber: leaf => lengthCheck(leaf.sampleNumber.length, leaf.delete) && duplicatesampleNumberCheck(leaf.sampleNumber),
        block: leaf => lengthCheck(leaf.blockNumber.length, leaf.delete),
        test: leaf => leaf.test != TestType.Unknown
    })

    function setDataIndex(index: number, update: ArrayUpdateType<SoilRow>) {
        props.setData(arrayUpdatePartial(props.leafs, index, update))
    }

    function setLeafData(update: (leaf: SoilRow) => SoilRow) {
        props.setData(props.leafs.map(l => update(l)))
    }

    function updateHeader<T>(value: T, setHeader: (value: T) => void, property: keyof SoilRow) {
        setHeader(value);
        setLeafData(l => {
            if (!l.selected)
                return l;
            return {...l, [property]: value};
        })
    }

    function updateHeaderSampleDepth(sampleDepth: SampleDepth) {
        updateHeader(sampleDepth, setHeaderSampleDepth, 'sampleDepth');
    }

    function updateHeaderTest(test: TestType) {
        updateHeader(test, setHeaderTest, 'test');
    }

    function updateHeaderOptionalTest(optionalTests: TestType[]) {
        updateHeader(optionalTests, setHeaderOptionalTests, 'optionalTests');
    }

    // Selection: sync with row's selected property
    const selectedKeys = props.leafs
        .map((l, i) => ({selected: l.selected, index: i}))
        .filter(l => l.selected)
        .map(l => l.index);

    function handleSelectionChange(keys: number[]) {
        props.setData(props.leafs.map((l, i) => ({...l, selected: keys.includes(i)})));
    }

    const columns: TableColumn<SoilRow>[] = useMemo(() => [
        {
            header: () => <div className="p-2 text-xs align-bottom">Sample Number</div>,
            row: (item, index) => {
                const leafValidation = validation.items[index];
                return (
                    <div className={classNames("table-data", item.delete ? 'inactive' : '')}>
                        <Input
                            onPaste={e => pasteInColumn(e, index, v => ({sampleNumber: v}), props.leafs, props.setData, emptySoilRow)}
                            className={leafValidation?.rules.sampleNumber ? '' : 'border-1 border-red-600'}
                            value={item.sampleNumber}
                            change={v => setDataIndex(index, {sampleNumber: v})}/>
                        {item.notes}
                    </div>
                );
            },
            sortValue: (item) => item.sampleNumber,
            key: 'sampleNumber'
        },
        {
            header: () => (
                <div className="p-2 text-xs align-bottom">
                    <div>Sample Depth</div>
                    <GenericSelect
                        options={props.data.sampleDepths}
                        textFunc={c => c.value}
                        valueFunc={c => c.key}
                        value={headerSampleDepth}
                        onChange={v => updateHeaderSampleDepth(v as SampleDepth)}/>
                </div>
            ),
            row: (item, index) => (
                <div className={classNames("table-data", item.delete ? 'inactive' : '')}>
                    <GenericSelect
                        options={props.data.sampleDepths}
                        textFunc={t => t.value}
                        valueFunc={t => t.key}
                        value={item.sampleDepth}
                        onChange={v => setDataIndex(index, {sampleDepth: v as SampleDepth})}/>
                </div>
            ),
            key: 'sampleDepth',
            sortValue: (item) => item.sampleDepth,
            cycleSortValues: [SampleDepth.TopSoil, SampleDepth.SubSoil, SampleDepth.TopSoilMicros]
        },
        {
            header: () => <div className="p-2 text-xs align-bottom">Block</div>,
            row: (item, index) => {
                const leafValidation = validation.items[index];
                return (
                    <div className={classNames("table-data", item.delete ? 'inactive' : '')}>
                        <Input
                            onPaste={e => pasteInColumn(e, index, v => ({blockNumber: v}), props.leafs, props.setData, emptySoilRow)}
                            className={leafValidation?.rules.block ? '' : 'border-1 border-red-600'}
                            value={item.blockNumber}
                            change={v => setDataIndex(index, {blockNumber: v})}/>
                    </div>
                );
            },
            sortValue: (item) => item.blockNumber,
            key: 'block'
        },
        {
            header: () => (
                <div className="p-2 text-xs align-bottom">
                    <div>Tests</div>
                    <GenericSelect
                        options={props.data.tests}
                        textFunc={c => c.value}
                        valueFunc={c => c.key}
                        value={headerTest}
                        onChange={v => updateHeaderTest(v as TestType)}/>
                </div>
            ),
            row: (item, index) => {
                const leafValidation = validation.items[index];
                return (
                    <GenericSelect
                        className={leafValidation?.rules.test ? '' : 'border-1 border-red-600'}
                        options={props.data.tests}
                        textFunc={c => c.value}
                        valueFunc={c => c.key}
                        value={item.test}
                        onChange={v => setDataIndex(index, {test: v as TestType})}/>
                );
            }
        },
        {
            header: () => (
                <div className="p-2 text-xs align-bottom">
                    <div>Optional Tests</div>
                    <GenericSelect
                        options={props.data.optionalTests}
                        textFunc={c => c.value}
                        valueFunc={c => c.key}
                        value={headerOptionalTests || []}
                        multiple={true}
                        onChange={v => updateHeaderOptionalTest(v as TestType[])}/>
                </div>
            ),
            row: (item, index) => (
                <GenericSelect
                    options={props.data.optionalTests}
                    textFunc={c => c.value}
                    valueFunc={c => c.key}
                    value={item.optionalTests || []}
                    multiple={true}
                    onChange={v => setDataIndex(index, {optionalTests: v as TestType[]})}/>
            )
        },
        {
            header: () => <div className="text-xs align-bottom">Actions</div>,
            row: (item, index) => (
                <div className="table-data">
                    {item.delete
                        ? <div className="btn-sm btn-primary" onClick={() => setDataIndex(index, {delete: false})}>+</div>
                        : <div className="btn-sm btn-error" onClick={() => setDataIndex(index, {delete: true})}>-</div>
                    }
                </div>
            )
        }
    ], [props.data, headerTest, headerSampleDepth, headerOptionalTests, validation.items]);

    return (
        <TableCore
            data={props.leafs}
            columns={columns}
            keyExtractor={(_, index) => index}
            headerClass="bg-gray-100"
            selection={{
                selectedKeys: selectedKeys,
                onSelectionChange: handleSelectionChange,
                selectAll: true
            }}
        />
    );
}

export default SubmissionTable;
